import i18next from "i18next";

import { EcgClassification } from "../../@types/Ecg";

// Tag variants available in the shared <Tag /> component.
type TagVariant =
  | "error"
  | "errorLight"
  | "tertiary"
  | "success"
  | "warning"
  | "info";

// EN-5191: translation keys, resolved on each call — a module constant holding t() output
// would be evaluated at import time, before i18next is initialised.
const ECG_CLASSIFICATION_LABEL_KEYS: Record<EcgClassification, string> = {
  // KAIv1
  normal: "ecg_classification_normal",
  atrialFibrillation: "ecg_classification_atrialFibrillation",
  unclassified: "ecg_classification_unclassified",
  tachycardia: "ecg_classification_tachycardia",
  bradycardia: "ecg_classification_bradycardia",
  short: "ecg_classification_short",
  long: "ecg_classification_long",
  unreadable: "ecg_classification_unreadable",
  noAnalysis: "ecg_classification_noAnalysis",
  // KAIv2
  sinusRhythm: "ecg_classification_sinusRhythm",
  sinusRhythmPacs: "ecg_classification_sinusRhythmPacs",
  sinusRhythmWideQrs: "ecg_classification_sinusRhythmWideQrs",
  sinusRhythmPvcs: "ecg_classification_sinusRhythmPvcs",
  // Fallback
  unknown: "ecg_classification_unknown",
};

// Colour grouping: normal rhythms = success (green), atrial fibrillation =
// error (red), other abnormal rhythms = tertiary (amber), and inconclusive
// results = info (grey).
// Per design (EN-4930): everything is red (error) except Normal (green) and
// the inconclusive results — No analysis, Unreadable, Too short — which are
// grey. `unknown` is our internal fallback, also treated as grey.
const ECG_CLASSIFICATION_VARIANTS: Record<EcgClassification, TagVariant> = {
  // Green
  normal: "success",

  // Grey
  noAnalysis: "info",
  unreadable: "info",
  short: "info",
  unknown: "info",

  // Red (everything else)
  atrialFibrillation: "error",
  unclassified: "error",
  tachycardia: "error",
  bradycardia: "error",
  long: "error",
  sinusRhythm: "error",
  sinusRhythmPacs: "error",
  sinusRhythmWideQrs: "error",
  sinusRhythmPvcs: "error",
};

export function getEcgClassificationLabel(
  classification: EcgClassification
): string {
  return i18next.t(
    ECG_CLASSIFICATION_LABEL_KEYS[classification] ??
      "ecg_classification_unknown"
  );
}

export function getEcgClassificationVariant(
  classification: EcgClassification
): TagVariant {
  return ECG_CLASSIFICATION_VARIANTS[classification] ?? "info";
}
